import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";

const STEPS = ["Mode & Location", "Specs & Price", "Amenities & Photos", "Contact & Submit"];
const AMENITY_OPTS = ["Lift", "Power Backup", "Gym", "Pool", "Security", "Parking", "Clubhouse", "Garden", "Pet Friendly", "Water Supply", "Central AC", "Furnished"];

export default function PostProperty() {
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", mode: "rent", category: "residential", property_type: "Apartment",
    city: "", locality: "", pin_code: "", state: "", address: "",
    bhk: "", bathrooms: 1, area_sqft: "", price: "", deposit: "", furnishing: "unfurnished", floor: "",
    amenities: [], images: ["", "", "", "", "", ""], description: "",
    owner_name: "", owner_phone: "",
  });

  useEffect(() => {
    if (!user) openAuth();
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, [user, openAuth]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAmenity = (a) =>
    set("amenities", form.amenities.includes(a) ? form.amenities.filter((x) => x !== a) : [...form.amenities, a]);

  const validStep = () => {
    if (step === 0) return form.title && form.city && form.pin_code;
    if (step === 1) return form.price && form.area_sqft;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/properties", {
        ...form,
        price: Number(form.price), deposit: Number(form.deposit) || 0,
        area_sqft: Number(form.area_sqft) || 0, bathrooms: Number(form.bathrooms) || 0,
        images: form.images.filter(Boolean),
      });
      toast.success("Property submitted for admin review!");
      navigate("/properties");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const cityDoc = locations.find((l) => l.city === form.city);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6" data-testid="post-property-page">
      <p className="eyebrow">Free listing</p>
      <h1 className="font-heading mt-2 text-3xl font-bold tracking-tight">Post Your Property</h1>

      <div className="mt-6 flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${i <= step ? "bg-blue-600 text-white" : "bg-secondary text-muted-foreground"}`}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <span className={`hidden text-xs font-medium sm:inline ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="h-px w-4 bg-border sm:w-8" />}
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border bg-card p-6">
        {step === 0 && (
          <div className="space-y-4" data-testid="post-step-0">
            <div className="space-y-1">
              <Label>Listing type</Label>
              <div className="grid grid-cols-3 gap-2">
                {[["rent", "For Rent"], ["buy", "Buy Requirement"], ["sale", "For Sale"]].map(([v, l]) => (
                  <button key={v} data-testid={`post-mode-${v}`} onClick={() => set("mode", v)}
                    className={`rounded-xl border-2 px-3 py-3 text-sm font-semibold transition-colors ${form.mode === v ? "border-blue-600 bg-blue-50 text-blue-700" : "border-border hover:border-blue-300"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label>Property title</Label>
              <Input data-testid="post-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Spacious 2BHK near metro" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="post-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="land">Land / Plot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Property type</Label>
                <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger data-testid="post-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Apartment", "Independent House", "Villa", "Builder Floor", "PG / Room", "Office Space", "Co-working Space", "Virtual Office", "Desks", "Shop", "Showroom", "Warehouse", "Residential Plot", "Commercial Plot", "Agricultural Land", "Industrial Property"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>City</Label>
                <Select value={form.city} onValueChange={(v) => { set("city", v); set("pin_code", ""); set("locality", ""); set("state", locations.find((l) => l.city === v)?.state || ""); }}>
                  <SelectTrigger data-testid="post-city-select"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.city} value={l.city}>{l.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Locality / PIN Code</Label>
                <Select value={form.pin_code} onValueChange={(v) => { set("pin_code", v); set("locality", cityDoc?.pincodes.find((p) => p.pin === v)?.locality || ""); }} disabled={!form.city}>
                  <SelectTrigger data-testid="post-pin-select"><SelectValue placeholder="Select PIN" /></SelectTrigger>
                  <SelectContent>
                    {(cityDoc?.pincodes || []).map((p) => <SelectItem key={p.pin} value={p.pin}>{p.locality} · {p.pin}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Address</Label>
              <Input data-testid="post-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Building / street / landmark" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="post-step-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>BHK</Label>
                <Select value={form.bhk} onValueChange={(v) => set("bhk", v)}>
                  <SelectTrigger data-testid="post-bhk-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5+ BHK"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Bathrooms</Label>
                <Input data-testid="post-bathrooms-input" type="number" min="0" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Area (sq.ft)</Label>
                <Input data-testid="post-area-input" type="number" value={form.area_sqft} onChange={(e) => set("area_sqft", e.target.value)} placeholder="1200" />
              </div>
              <div className="space-y-1">
                <Label>{form.mode === "rent" ? "Monthly rent (₹)" : "Price (₹)"}</Label>
                <Input data-testid="post-price-input" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder={form.mode === "rent" ? "25000" : "7500000"} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Furnishing</Label>
                <Select value={form.furnishing} onValueChange={(v) => set("furnishing", v)}>
                  <SelectTrigger data-testid="post-furnishing-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fully-furnished">Fully Furnished</SelectItem>
                    <SelectItem value="semi-furnished">Semi Furnished</SelectItem>
                    <SelectItem value="unfurnished">Unfurnished</SelectItem>
                    <SelectItem value="raw">Raw / Bare Shell</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Floor (optional)</Label>
                <Input data-testid="post-floor-input" value={form.floor} onChange={(e) => set("floor", e.target.value)} placeholder="e.g. 4 of 12" />
              </div>
            </div>
            {form.mode === "rent" && (
              <div className="space-y-1">
                <Label>Security deposit (₹)</Label>
                <Input data-testid="post-deposit-input" type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} placeholder="75000" />
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="post-step-2">
            <div>
              <Label>Amenities</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {AMENITY_OPTS.map((a) => (
                  <button key={a} data-testid={`amenity-${a.replace(/\s/g, "-")}`} onClick={() => toggleAmenity(a)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${form.amenities.includes(a) ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "hover:border-emerald-400"}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Photo URLs (up to 6)</Label>
              <div className="mt-2 space-y-2">
                {form.images.map((img, i) => (
                  <Input key={i} data-testid={`post-image-input-${i}`} value={img} placeholder={`Photo ${i + 1} URL`}
                    onChange={(e) => set("images", form.images.map((x, j) => (j === i ? e.target.value : x)))} />
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea data-testid="post-description-input" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Highlights, nearby landmarks, possession status..." />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="post-step-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Owner / Advertiser name</Label>
                <Input data-testid="post-owner-name-input" value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} placeholder={user?.name || "Your name"} />
              </div>
              <div className="space-y-1">
                <Label>Contact phone</Label>
                <Input data-testid="post-owner-phone-input" value={form.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} placeholder="+91 98XXX XXXXX" />
              </div>
            </div>
            <div className="rounded-xl bg-amber-50 p-4 text-xs leading-relaxed text-amber-800">
              Your listing will be submitted for admin review. Once approved it goes live with a verification review. Ensure you are the owner or an authorized advertiser.
            </div>
            <div className="rounded-xl border p-4 text-sm">
              <p className="font-heading font-bold">{form.title || "Untitled"}</p>
              <p className="mt-1 text-muted-foreground">{form.property_type} · {form.bhk || "—"} · {form.city}{form.pin_code ? ` · ${form.pin_code}` : ""}</p>
              <p className="font-price mt-1 font-bold text-emerald-700">{form.mode === "rent" ? `₹${Number(form.price || 0).toLocaleString("en-IN")}/mo` : `₹${Number(form.price || 0).toLocaleString("en-IN")}`}</p>
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-between">
          <Button data-testid="post-back-button" variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} className="gap-1">
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
          {step < 3 ? (
            <Button data-testid="post-next-button" disabled={!validStep()} onClick={() => setStep(step + 1)} className="gap-1">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button data-testid="post-submit-button" disabled={busy} onClick={submit} className="bg-emerald-600 text-white hover:bg-emerald-700">
              {busy ? "Submitting..." : "Submit for Review"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
